import React, { useContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import removeAccents from 'remove-accents';
import { connect } from 'react-redux';
import { withStyles } from '@glu/theming';
import fieldActions from 'core/fields/actions';
import { GooglePlacesAutocompleteContext } from 'contexts/GooglePlacesAutocompleteContext';
import buttonLinkStyles from 'styles/buttonLink';
import visuallyHidden from 'styles/visuallyHidden';

const styles = ({ links, oaoButtons: buttons, palette }) => ({
  active: {},
  googleLogo: {
    position: 'absolute',
    right: 3,
    top: -25,
  },
  link: {
    ...buttonLinkStyles({ links }),
    '&:focus, &$active': {
      background: buttons.primary.hoverBackground,
      color: 'white',
      textDecoration: 'none',
    },
    '&:hover': {
      background: buttons.primary.hoverBackground,
      color: buttons.primary.hoverColor,
      textDecoration: 'none',
    },
    cursor: 'pointer',
    float: 'left',
    padding: [3, 5],
    textDecoration: 'none',
    width: '100%',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  root: {
    '& li': {
      padding: 0,
    },
    background: palette.background,
    borderRadius: 2,
    boxShadow: '0 0 5px rgba(0, 0, 0, 0.5)',
    marginTop: 2,
    padding: 0,
    position: 'absolute',
    top: 'auto',
    width: '100%',
    zIndex: 10,
  },
  visuallyHidden,
});

const sanitizeString = (string) => (string ? removeAccents(string) : string);

const AutocompleteMenu = ({
  activeItemIndex,
  classes,
  clientId,
  fieldInput,
  googlePlacesAutocomplete,
  hasFocus,
  keyUpEvent,
  setActiveItemIndex,
}) => {
  const [data, setData] = useState([]);
  const [didUpdate, setDidUpdate] = useState(false);
  const rootNode = useRef();
  const googleServices = useContext(GooglePlacesAutocompleteContext);

  useEffect(() => {
    if (!googleServices.sessionToken) {
      googleServices.generateToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDetailsResponse = ({ address_components: addressComponents }) => {
    const clientIdPrefix = `${clientId
      .split('_')
      .filter((v, i, arr) => i !== arr.length - 1)
      .join('_')}_`;
    const clientIdIndex = clientId.split(':').length > 1 ? clientId.split(':')[1] : null;

    if (addressComponents) {
      const place = addressComponents.reduce(
        (obj, item) => ({
          ...obj,
          [item.types[0]]:
            item.types[0] === 'administrative_area_level_1' ? item.short_name : item.long_name,
        }),
        {},
      );

      const payload = {
        addressLine1:
          place.street_number || place.route
            ? `${place.street_number || ''} ${place.route || ''}`
            : null,
        city: place.locality || place.sublocality_level_1 || null,
        country: place.country || null,
        postalCode:
          `${place.postal_code}${place.postal_code_suffix ? `-${place.postal_code_suffix}` : ''}` ||
          null,
        state: place.administrative_area_level_1 || null,
      };
      const sanitizedPayload = Object.keys(payload)
        .map((key) => key)
        .reduce((obj, item) => ({ ...obj, [item]: sanitizeString(payload[item]) }), {});

      googlePlacesAutocomplete(clientIdPrefix, sanitizedPayload, clientIdIndex);
    }
  };

  const onClick = (e, item) => {
    if (e) {
      e.preventDefault();
    }
    setDidUpdate(true);
    setData([]);
    googleServices.placesService.getDetails(
      {
        fields: ['address_component'],
        placeId: item.place_id,
      },
      (place) => handleDetailsResponse(place),
    );
  };

  useEffect(() => {
    setActiveItemIndex(-1);
  }, [data]);

  useEffect(() => {
    const event = keyUpEvent ? keyUpEvent.split(':')[0] : '';

    if (data && event) {
      switch (event) {
        case 'ArrowUp':
          if (activeItemIndex < 1) {
            setActiveItemIndex(data.length - 1);
          } else {
            setActiveItemIndex((i) => i - 1);
          }
          break;
        case 'ArrowDown':
          if (activeItemIndex >= data.length - 1) {
            setActiveItemIndex(0);
          } else {
            setActiveItemIndex((i) => i + 1);
          }
          break;
        case 'Enter':
          if (data[activeItemIndex]) {
            onClick(null, data[activeItemIndex]);
          }
          break;
        case 'blur':
          // Slightly delay the menu closing
          setTimeout(() => setData([]), 200);
          break;
        default:
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyUpEvent]);

  useEffect(() => {
    if (googleServices.enabled && fieldInput && !didUpdate && hasFocus) {
      googleServices.autocompleteService.getPlacePredictions(
        {
          componentRestrictions: { country: 'us' },
          input: fieldInput,
          sessionToken: googleServices.sessionToken,
          types: ['address'],
        },
        (rs) => {
          setData(rs);
        },
      );
    }
    if (didUpdate) {
      setDidUpdate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fieldInput,
    googleServices.autocompleteService,
    googleServices.enabled,
    googleServices.sessionToken,
    hasFocus,
  ]);

  return (
    googleServices.enabled &&
    data &&
    data.length > 0 && (
      <>
        <div ref={rootNode} className={classnames('autocomplete-menu', classes.root)}>
          <ul className={classnames('autocomplete-list', classes.list)}>
            {data.map((item, i) => (
              <li
                id={`${clientId}-item${i}`}
                key={item.id || item.place_id}
                role="option"
                aria-selected={activeItemIndex === i}
                data-qa={item.id || item.place_id}
                tabIndex="-1"
              >
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <button
                  type="button"
                  tabIndex="-1"
                  className={classnames('buttonLink', classes.link, {
                    [classes.active]: activeItemIndex === i,
                  })}
                  onClick={(e) => {
                    onClick(e, item);
                  }}
                >
                  {item.description}
                </button>
              </li>
            ))}
          </ul>
          <img
            src="/oao/images/powered_by_google_on_white.png"
            alt="Powered By Google"
            className={classnames('powered-by-google', classes.googleLogo)}
          />
        </div>
        <div role="alert" className={classes.visuallyHidden}>
          {`${data.length} results available.`}
        </div>
      </>
    )
  );
};

AutocompleteMenu.propTypes = {
  activeItemIndex: PropTypes.number,
  classes: PropTypes.shape({}).isRequired,
  clientId: PropTypes.string.isRequired,
  fieldInput: PropTypes.string.isRequired,
  googlePlacesAutocomplete: PropTypes.func.isRequired,
  hasFocus: PropTypes.bool,
  keyUpEvent: PropTypes.string,
  setActiveItemIndex: PropTypes.func,
};

AutocompleteMenu.defaultProps = {
  activeItemIndex: -1,
  hasFocus: false,
  keyUpEvent: null,
  setActiveItemIndex: () => {},
};

const StyledAutocompleteMenu = withStyles(styles)(AutocompleteMenu);

const mapStateToProps = () => ({});

const mapDispatchToProps = {
  googlePlacesAutocomplete: fieldActions.googlePlacesAutocomplete,
};

export default connect(mapStateToProps, mapDispatchToProps)(StyledAutocompleteMenu);
